import { normalizeSalesImportBranchId } from "../config/salesImportBranchIds";
import { normalizeTranDateString } from "../utils/dateParse";
import { ensureValidDocId, isValidFirestoreDocId, salesTransactionDocId } from "../utils/docId";

function isEmpty(v) {
  return v == null || String(v).trim() === "";
}

/**
 * @param {object} row
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateSalesRow(row) {
  const errors = [];
  if (isEmpty(row.billNo)) errors.push("BillNo cannot be empty");
  if (isEmpty(row.partNo) && isEmpty(row.itemName)) errors.push("PartNo / ItemName cannot be empty");
  if (isEmpty(row.productId)) errors.push("ProductID cannot be empty");

  const qty = Number(row.qty);
  if (!Number.isFinite(qty) || qty === 0) errors.push("Qty cannot be zero");

  const amount = Number(row.amount);
  if (!Number.isFinite(amount)) errors.push("Amount must be a valid number");

  const unitPrice = Number(row.unitPrice);
  if (!Number.isFinite(unitPrice) || unitPrice < 0) errors.push("UnitPrice must be >= 0");

  const mrp = Number(row.mrp);
  if (row.mrp != null && row.mrp !== "" && (!Number.isFinite(mrp) || mrp < 0)) {
    errors.push("MRP must be >= 0");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate all rows with chunked progress (keeps UI responsive).
 * @param {object[]} rows
 * @param {(done: number, total: number) => void} [onProgress]
 */
export async function validateAllRows(rows, onProgress) {
  const CHUNK = 2000;
  const validated = [];
  let validCount = 0;
  let invalidCount = 0;
  const seenIds = new Map();

  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK);
    for (const row of slice) {
      const { valid: baseValid, errors } = validateSalesRow(row);
      const docId = salesTransactionDocId(row.billNo, row.productId);
      let rowValid = baseValid;
      if (!isValidFirestoreDocId(docId)) {
        errors.push("Invalid document ID (check Bill No and Product ID)");
        rowValid = false;
      }

      let duplicateInFile = false;
      if (docId && docId !== "_") {
        if (seenIds.has(docId)) duplicateInFile = true;
        else seenIds.set(docId, true);
      }

      const entry = {
        ...row,
        docId: ensureValidDocId(docId, row.billNo, row.productId),
        valid: rowValid,
        errors,
        duplicateInFile,
      };
      if (entry.valid) validCount += 1;
      else invalidCount += 1;
      validated.push(entry);
    }
    if (onProgress) onProgress(Math.min(i + CHUNK, rows.length), rows.length);
    await new Promise((r) => setTimeout(r, 0));
  }

  const duplicateInFileCount = validated.filter((r) => r.duplicateInFile).length;

  return {
    rows: validated,
    validCount,
    invalidCount,
    duplicateInFileCount,
    uniqueValidCount: validCount,
  };
}

/**
 * @param {object} audit weekNumber, fromDate, toDate, importBatchId, fileName
 */
export function rowToFirestorePayload(row, uploadedBy, uploadedAtIso, audit = {}) {
  return {
    voucherType: row.voucherType ?? "",
    voucherNo: row.voucherNo ?? "",
    mVoucherNo: row.mVoucherNo ?? "",
    billNo: row.billNo ?? "",
    tranDate: normalizeTranDateString(row.tranDate ?? "") || String(row.tranDate ?? ""),
    customerName: row.customerName ?? "",
    employeeName: row.employeeName ?? "",
    staffName: row.staffName ?? "",
    itemGroup: row.itemGroup ?? "",
    brandName: row.brandName ?? row.brand ?? "",
    itemName: row.itemName ?? "",
    alias: row.alias ?? "",
    partNo: row.partNo ?? "",
    productId: String(row.productId ?? ""),
    qty: Number(row.qty) || 0,
    unitPrice: Number(row.unitPrice) || 0,
    mrp: Number(row.mrp) || 0,
    discount: Number(row.discount) || 0,
    discountAmt: Number(row.discountAmt) || 0,
    amount: Number(row.amount) || 0,
    billAmount: Number(row.billAmount) || 0,
    profitCentre: row.profitCentre ?? row.profitPercent ?? "",
    profitPercent: row.profitPercent ?? row.profitCentre ?? "",
    workstationId: row.workstationId ?? "",
    branchId: normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? ""),
    uploadedAt: uploadedAtIso,
    uploadedBy: uploadedBy ?? "",
    weekNumber: audit.weekNumber ?? audit.periodKey ?? "",
    periodKey: audit.periodKey ?? audit.weekNumber ?? "",
    year: audit.year ?? null,
    month: audit.monthIndex != null ? audit.monthIndex + 1 : null,
    weekOfMonth: audit.weekOfMonth ?? null,
    fromDate: audit.fromDate ?? "",
    toDate: audit.toDate ?? "",
    importBatchId: audit.importBatchId ?? "",
    fileName: audit.fileName ?? "",
  };
}
